import React, { useCallback } from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { PiPlus } from "react-icons/pi";
import { DropDownButtonComponent } from "@syncfusion/ej2-react-splitbuttons";
import { Button } from "@/components/buttons";
import { useOverlay, OVERLAY_SIZES } from "@/components/overlays";
import CoBorrowerModal from "@/modules/client/loan-application/features/loan-origination/sub-features/additional-details/components/co-borrower-modal";
import DeleteCoBorrowerModal from "@/modules/client/loan-application/features/loan-origination/sub-features/additional-details/components/delete-co-borrower-modal";

/**
 * CoBorrowerList — displays added co-borrowers and actions to add/edit/delete.
 * Uses the overlay system instead of legacy Redux modals.
 * Uses Syncfusion DropDownButtonComponent instead of HeadlessUI Menu.
 */
export default function CoBorrowerList() {
  const { control } = useFormContext();
  const { fields, append, remove, update } = useFieldArray({ control, name: "coApplicants" });
  const { openModal } = useOverlay();

  const handleAdd = useCallback(() => {
    openModal("CO_BORROWER_FORM", CoBorrowerModal, { onSave: data => append(data) }, { size: OVERLAY_SIZES.LG });
  }, [openModal, append]);

  const handleEdit = useCallback((index, coApplicant) => {
    openModal("CO_BORROWER_FORM", CoBorrowerModal, {
      defaultValues: coApplicant,
      onSave: data => update(index, data),
    }, { size: OVERLAY_SIZES.LG });
  }, [openModal, update]);

  const handleDelete = useCallback((index, coApplicant) => {
    openModal("DELETE_CO_BORROWER", DeleteCoBorrowerModal, {
      coApplicant,
      onConfirm: () => remove(index),
    }, { size: OVERLAY_SIZES.SM });
  }, [openModal, remove]);

  const actionItems = [{ text: "Edit", iconCss: "e-icons e-edit" }, { text: "Delete", iconCss: "e-icons e-trash" }];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Co-Borrowers</h3>
          <p className="text-sm text-gray-500 dark:text-slate-400">Add co-borrowers to this application.</p>
        </div>
        <Button type="button" variant="primary" size="sm" label="Add Co-Borrower" icon={<PiPlus className="size-4" />} onClick={handleAdd} />
      </div>

      {fields.length === 0 && (
        <p className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-400 dark:border-slate-600 dark:text-slate-500">
          No co-borrowers added yet.
        </p>
      )}

      <ul className="divide-y divide-gray-100 dark:divide-slate-700">
        {fields.map((coApplicant, index) => (
          <li key={coApplicant.id} className="flex items-center justify-between gap-x-4 py-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {coApplicant.firstName} {coApplicant.lastName}
              </p>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">{coApplicant.email}</p>
            </div>
            <DropDownButtonComponent
              items={actionItems}
              cssClass="e-flat e-small e-round e-caret-hide"
              content="⋮"
              select={e => {
                if (e.item.text === "Edit") handleEdit(index, coApplicant);
                if (e.item.text === "Delete") handleDelete(index, coApplicant);
              }}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

